<template>
  <v-app v-if="initialized">
    <!-- Bypass Blocks (WCAG 2.4.1): erstes fokussierbares Element der Seite,
         sichtbar erst bei Tastaturfokus. -->
    <a
      class="cc-skip-link"
      href="#main-content"
    >
      {{ t('common.a11y.skipToContent') }}
    </a>
    <v-navigation-drawer
      :permanent="!mobile"
      :temporary="mobile"
      :fixed="mobile"
      app
      v-model="showSideNav"
    >
      <v-img
        :src="logoUrl()"
        :alt="t('common.a11y.logo')"
      />
      <v-list
        nav
        data-v-main-step="3"
      >
        <template
          v-for="(item, i) in items"
          :key="i"
        >
          <v-list-group
            v-if="item.hasOwnProperty('items')"
            :prepend-icon="item.icon"
          >
            <template #activator="{props}">
              <v-list-item v-bind="props">
                <v-list-item-title>{{ item.title }}</v-list-item-title>
              </v-list-item>
            </template>
            <v-list-item
              v-for="(subItem, subI) in item.items"
              :key="subI"
              :to="{name: subItem.path}"
              :class="isActive(subItem) ? 'v-list-item--active pl-5' : 'pl-5'"
              exact
            >
              <template #prepend>
                <v-icon :icon="subItem.icon" />
              </template>
              <v-list-item-title>
                {{ subItem.title }}
              </v-list-item-title>
            </v-list-item>
          </v-list-group>
          <v-list-item
            v-else
            :to="{name: item.path}"
            :class="`${(isActive(item) ? 'v-list-item--active' : '')} ${item.hasOwnProperty('css') ? item.css : ''}`"
            exact
          >
            <template #prepend>
              <v-icon :icon="item.icon" />
            </template>
            <v-list-item-title>
              {{ item.title }}
            </v-list-item-title>
          </v-list-item>
        </template>
        <v-list-item
          tag="a"
          @click="logout()"
          class="d-flex d-md-none"
        >
          <template #prepend>
            <v-icon :icon="Icons.LOGOUT" />
          </template>
          <v-list-item-title>
            {{ t('navigation.logout') }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>
    <v-app-bar
      :absolute="true"
      app
    >
      <v-spacer />
      <v-tooltip location="bottom">
        <template #activator="{ props }">
          <v-icon
            v-bind="props"
            :icon="Icons.BULLHORN_OUTLINE"
            v-if="store.isAdmin"
            class="mr-7"
            @click="moveToWhatsNew"
            :aria-label="t('navigation.tooltip.whatsNew')"
            @keydown.enter="moveToWhatsNew"
            @keydown.space.prevent="moveToWhatsNew"
          />
        </template>
        <span>{{ t('navigation.tooltip.whatsNew') }}</span>
      </v-tooltip>
      <v-tooltip location="bottom">
        <template #activator="{ props }">
          <v-icon
            v-bind="props"
            v-if="store.isAdmin"
            @click="startMainTour"
            class="d-none d-md-block mr-7"
            :icon="Icons.HELP_CIRCLE_OUTLINE"
            :aria-label="t('common.help')"
            @keydown.enter="startMainTour"
            @keydown.space.prevent="startMainTour"
          />
        </template>
        <span>{{ t('common.help') }}</span>
      </v-tooltip>
      <v-tooltip location="bottom">
        <template #activator="{ props }">
          <v-icon
            v-bind="props"
            class="mr-7"
            v-if="store.userData?.user_switch_name"
            @click="returnUser()"
            :icon="Icons.ACCOUNT_SWITCH"
            :aria-label="t('navigation.tooltip.userSwitch', {name: store.userData?.user_switch_name})"
            @keydown.enter="returnUser()"
            @keydown.space.prevent="returnUser()"
          />
        </template>
        <span>{{ t('navigation.tooltip.userSwitch', {name: store.userData?.user_switch_name}) }}</span>
      </v-tooltip>
      <user-switch-modules />
      <menu-partner-shops />
      <menu-calendar />
      <menu-notifications />
      <v-menu offset-y>
        <template #activator="{props}">
          <cc-image
            v-if="store.userData?.image?.name"
            v-bind="props"
            class="d-none d-md-block mr-3"
            data-v-main-step="0"
            :file="store.userData?.image?.name"
            :mime-type="store.userData?.image?.mimeType"
            :path="`user/${store.userData?.user ?? ''}`"
            round
            :size="30"
          />
          <v-icon
            v-else
            v-bind="props"
            size="large"
            class="d-none d-md-block mr-3"
            data-v-main-step="0"
            :icon="Icons.ACCOUNT_CIRCLE"
          />
        </template>
        <v-list>
          <v-list-item :to="{name: 'userProfile'}">
            <template #prepend>
              <v-icon :icon="Icons.ACCOUNT_CIRCLE" />
            </template>
            <v-list-item-title>
              {{ t('navigation.profile') }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item
            v-if="store.userData?.main_user"
            :to="{name: 'coachingProfile'}"
          >
            <template #prepend>
              <v-icon :icon="Icons.CARD_ACCOUNT_DETAILS_OUTLINE" />
            </template>
            <v-list-item-title>
              {{ t('navigation.coachingProfile') }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item
            v-if="store.userData?.main_user"
            :to="{name: 'coachingProfileSearch'}"
          >
            <template #prepend>
              <v-icon :icon="Icons.ACCOUNT_SEARCH_OUTLINE" />
            </template>
            <v-list-item-title>
              {{ t('navigation.coachingProfileSearch') }}
            </v-list-item-title>
          </v-list-item>
          <UserSwitch />
          <v-divider />
          <v-list-item
            tag="a"
            @click="logout()"
          >
            <template #prepend>
              <v-icon :icon="Icons.LOGOUT" />
            </template>
            <v-list-item-title>
              {{ t('navigation.logout') }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <cc-image
        v-if="store.userData?.image?.name"
        @click="showSideNav = !showSideNav"
        class="d-block d-md-none mr-3 m-sm-0"
        :file="store.userData?.image?.name"
        :mime-type="store.userData?.image?.mimeType"
        :path="`user/${store.userData?.user ?? ''}`"
        round
        :size="30"
      />
      <v-icon
        v-else
        class="d-block d-md-none mr-3 m-sm-0"
        :aria-label="t('common.a11y.openProfileMenu')"
        @click="showSideNav = !showSideNav"
        @keydown.enter="showSideNav = !showSideNav"
        @keydown.space.prevent="showSideNav = !showSideNav"
        size="large"
        :icon="Icons.ACCOUNT_CIRCLE"
      />
    </v-app-bar>
    <v-main id="main-content">
      <router-view />
      <v-onboarding-wrapper
        ref="mainOnboardingWrapper"
        v-if="store.isAdmin"
        :steps="tourSteps"
        :options="tourOptions"
      />
    </v-main>
    <!-- T009OSKBRBOH — a running 1:1 call stays when the page changes. -->
    <coaching-call-host />
    <v-footer
      color="primary"
      data-v-main-step="4"
    >
      <v-card
        color="primary"
        width="100%"
        flat
        :class="!mobile ? 'cc-footer-card' : ''"
      >
        <v-card-text
          class="text-center"
          v-if="socialMedias.length > 0"
        >
          <v-btn
            variant="text"
            size="large"
            :href="socialMedia.url"
            target="_blank"
            v-for="socialMedia in socialMedias"
            :key="socialMedia.id"
            :icon="Icons[socialMedia.icon.toUpperCase() as keyof typeof Icons]"
            :aria-label="socialMedia.icon"
          />
        </v-card-text>
        <v-card-text
          class="text-center"
          v-if="footerLinks.length > 0"
        >
          <a
            :href="footerLink.url"
            target="_blank"
            :class="`text-h6 ${footerLinks.length > 1 ? 'mr-10' : ''}`"
            v-for="footerLink in footerLinks"
            :key="footerLink.id"
            v-dompurify-html="footerLink.name"
          />
        </v-card-text>
      </v-card>
    </v-footer>

    <!-- T00B8BPCQBCH — Wirtskomponente für Dialoge und Toasts aus
         client-alerts. Muss innerhalb von <v-app> stehen, damit Theme,
         Overlay-Stapel und Scroll-Sperre von Vuetify greifen. -->
    <alerts-host />
  </v-app>
</template>

<script setup lang="ts">
//================= [CSS] =====================================

//================= [JS] ======================================
import {SecUtil} from '@/common/utils/security/SecUtil';
import {Icons, LoginManager, VOnboardingWrapper, tourOptions, useVOnboarding} from '@simpleforyou/client-core';
//================= [VUE COMPONENTS] ==========================
import CoachingCallHost from '@/components/individualCoaching/call/CoachingCallHost.vue';
import UserSwitch from '@/components/admin/UserSwitch.vue';
import UserSwitchModules from '@/components/admin/UserSwitchModules.vue';
import {footerLinks, logoUrl, returnUser, socialMedias} from '@/composables/layout/baseLayout';
import {initialized} from '@/composables/layout/layoutHandler';
import {computed, onMounted, ref} from 'vue';
import {useRouter} from 'vue-router';
import {useI18n} from 'vue-i18n';
import MenuPartnerShops from '@/layout/menu/MenuPartnerShops.vue';
import MenuCalendar from '@/layout/menu/MenuCalendar.vue';
import MenuNotifications from '@/layout/menu/MenuNotifications.vue';
import CcImage from '@/components/common/file/CcImage.vue';
import {useDisplay} from 'vuetify';
import {useStore} from '@/common/store/store';
import {useAuth} from '@/composables/useAuth';
import {Permissions} from '@simpleforyou/coaching-shared';
const {logout} = useAuth();

const mainOnboardingWrapper = ref(null);
const {start} = useVOnboarding(mainOnboardingWrapper);

const router = useRouter();
const {t} = useI18n();
const store = useStore();

const {mobile} = useDisplay();
const showSideNav = ref(true);

const tourSteps = [
  {
    attachTo: {
      element: '[data-v-main-step="0"]',
    },
    content: {
      description: t('layout.tour.steps.0.content'),
    },
  },
  {
    attachTo: {
      element: '[data-v-main-step="1"]',
    },
    content: {
      description: t('layout.tour.steps.1.content'),
    },
  },
  {
    attachTo: {
      element: '[data-v-main-step="2"]',
    },
    content: {
      description: t('layout.tour.steps.2.content'),
    },
  },
  {
    attachTo: {
      element: '[data-v-main-step="3"]',
    },
    content: {
      description: t('layout.tour.steps.3.content'),
    },
  },
  {
    attachTo: {
      element: '[data-v-main-step="4"]',
    },
    content: {
      description: t('layout.tour.steps.4.content'),
    },
  },
];

const items = computed(() => {
  const navigation = [];
  // Every coach sees the contract; only the main user can change it (the API decides).
  // First entry: the contract decides what the rest of the menu offers.
  if (store.isAdmin) {
    navigation.push({
      title: t('navigation.contract'),
      path: 'contract',
      icon: Icons.FILE_SIGN,
      active: 'contract',
    });
  }
  if (SecUtil.hasRoles(Permissions.ROLE_CUSTOMER_INDEX)) {
    navigation.push({
      title: t('navigation.customer'),
      path: 'customerList',
      icon: Icons.BRIEFCASE_ACCOUNT,
      active: 'customer',
    });
  }
  if (SecUtil.hasRoles(Permissions.ROLE_MODULE_INDEX)) {
    navigation.push({title: t('navigation.module'), path: 'moduleList', icon: Icons.BALLOT, active: 'module'});
  }
  // T00MULLHA0HY — the role alone is not enough, the COURSE module must be booked.
  if (SecUtil.coaching('course') && SecUtil.hasRoles(Permissions.ROLE_COURSE_INDEX)) {
    navigation.push({
      title: t('navigation.course'),
      path: 'courseList',
      icon: Icons.FILE_COMPARE,
      active: 'course',
    });
  }
  if (SecUtil.hasRoles(Permissions.ROLE_USER_INDEX)) {
    navigation.push({title: t('navigation.user'), path: 'userList', icon: Icons.ACCOUNT_BOX, active: 'user'});
  }
  if (SecUtil.hasRoles(Permissions.ROLE_FILE_SYSTEM_INDEX)) {
    navigation.push({
      title: t('navigation.fileSystem'),
      path: 'fileSystemList',
      icon: Icons.FILE_MULTIPLE,
      active: 'fileSystem',
    });
  }
  // E00U6EKA5EGC — the coach's client rooms, while the module is on.
  if (SecUtil.coaching('individualCoaching') && store.isAdmin) {
    navigation.push({
      title: t('navigation.individualCoaching'),
      path: 'individualCoaching',
      icon: Icons.ACCOUNT_GROUP,
      active: 'individualCoaching',
    });
  }
  if (SecUtil.hasRoles(Permissions.ROLE_LIVE_COACHING_INDEX)) {
    navigation.push({
      title: t('navigation.liveCoaching'),
      path: 'liveCoaching',
      icon: Icons.HEADSET,
      active: 'liveCoaching',
    });
  }
  if (SecUtil.coaching('recipe')) {
    navigation.push({
      title: t('navigation.recipe.label'), icon: Icons.CHEF_HAT,
      active: ['recipe', 'cookbook'],
      items: [
        {title: t('navigation.recipe.recipeList'), path: 'recipeList', icon: Icons.CHEF_HAT, active: 'recipe'},
        {
          title: t('navigation.recipe.cookBook'),
          path: 'cookbook',
          icon: Icons.BOOK_OPEN_VARIANT,
          active: 'cookbook',
        },
      ],
    });
  }
  if (SecUtil.coaching('statistic') && store.isAdmin) {
    navigation.push({
      title: t('navigation.statistic'),
      path: 'statistic',
      icon: Icons.CHART_LINE,
      active: 'statistic',
    });
  }
  if (SecUtil.hasRoles(Permissions.ROLE_NOTIFICATION_INDEX)) {
    navigation.push({
      title: t('navigation.notification.label'),
      path: 'notification',
      icon: Icons.BELL,
      active: 'notification',
    });
  }
  if (SecUtil.hasRoles(Permissions.ROLE_MAINTENANCE_INDEX)) {
    navigation.push({
      title: t('navigation.maintenance'),
      path: 'maintenance',
      icon: Icons.WRENCH_OUTLINE,
      active: 'maintenance',
    });
  }
  if (SecUtil.hasRoles(Permissions.ROLE_CHANGELOG_INDEX)) {
    navigation.push({
      title: t('navigation.changelog'),
      path: 'changelogList',
      icon: Icons.BULLHORN_OUTLINE,
      active: 'changelog',
    });
  }
  if (SecUtil.hasRoles(Permissions.ROLE_CUSTOMER_SETTINGS_INDEX)) {
    navigation.push({
      title: t('navigation.settings'),
      path: 'settings',
      icon: Icons.COG_OUTLINE,
      active: 'settings',
    });
  }
  // add profile link to navigation only for mobile size
  navigation.push({
    title: t('navigation.profile'),
    css: 'd-flex d-md-none',
    path: 'userProfile',
    icon: Icons.CARD_ACCOUNT_DETAILS_OUTLINE,
    active: 'profile',
  });

  return navigation;
});

const isActive = (item: any) => {
  if (!router.currentRoute.value.meta.hasOwnProperty('active')) {
    return false;
  }

  if (Array.isArray(item.active)) {
    return item.active.includes(router.currentRoute.value.meta.active);
  } else {
    return router.currentRoute.value.meta.active === item.active;
  }
};
const startMainTour = () => {
  start();
};
const moveToWhatsNew = () => {
  router.push({name: 'whatsNew'});
};

onMounted(async () => {
  if (!LoginManager.INSTANCE.isLoggedIn()) {
    return;
  }
});

</script>

<style scoped>

</style>
